import React, { useState, useEffect, useRef } from 'react';

const ITEM_HEIGHT = 40;

// SlotColumn is intentionally defined OUTSIDE the SlotMachine component
// so React preserves its identity across parent re-renders. If it were
// nested inside, every parent render would create a new component type,
// remounting the column and wiping out local state (offset, lockedOffsetRef),
// which caused locked wheels to drift / reset.
const SlotColumn = ({ title, type, isLocked, dataArray, isSpinning, onToggleLock, onNudge, selectedValues }) => {
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState([]);
  const animationRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const lockedOffsetRef = useRef(null);
  const previousIsLockedRef = useRef(isLocked);

  // Initialize items array (re-runs when dataArray changes, e.g. when
  // App.js injects a new IGDB-returned value into the wheel list).
  useEffect(() => {
    const repeatedArray = [...dataArray, ...dataArray, ...dataArray, ...dataArray];
    setItems(repeatedArray);
    setIsInitialized(true);
  }, [dataArray]);

  // When dataArray changes (e.g. App.js injects a new IGDB-returned
  // value into the list), re-anchor offset based on the current selected
  // value. Prepending shifts indices by +1, so locked wheels MUST
  // recompute their absolute offset to stay visually anchored. This runs
  // even mid-spin for locked wheels so the lock holds correctly.
  useEffect(() => {
    if (!isInitialized) return;
    const currentValue = selectedValues && selectedValues[type];
    if (!currentValue) return;
    const idx = dataArray.indexOf(currentValue);
    if (idx === -1) return;
    const targetOffset = idx * ITEM_HEIGHT;
    if (isLocked) {
      // For locked wheels, re-anchor immediately regardless of spin state.
      lockedOffsetRef.current = targetOffset;
      setOffset(targetOffset);
    } else if (!isSpinning) {
      // Unlocked + idle: snap to the new index too.
      setOffset(targetOffset);
    }
    // Skip when unlocked + spinning — the spin animation owns the offset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataArray]);

  // Capture lock transitions: snap the locked offset to the currently
  // selected value (if any) so the visible item matches state.
  useEffect(() => {
    const wasLocked = previousIsLockedRef.current;
    if (!wasLocked && isLocked) {
      // Just became locked. Prefer the currently selected value's index.
      const currentValue = selectedValues && selectedValues[type];
      if (currentValue && dataArray.indexOf(currentValue) !== -1) {
        const idx = dataArray.indexOf(currentValue);
        lockedOffsetRef.current = idx * ITEM_HEIGHT;
        setOffset(idx * ITEM_HEIGHT);
      } else {
        // No selected value yet (locked before first spin) — freeze where we are,
        // snapped to nearest item for a clean visual.
        const snapped = Math.round(offset / ITEM_HEIGHT) * ITEM_HEIGHT;
        lockedOffsetRef.current = snapped;
        setOffset(snapped);
      }
    }
    if (wasLocked && !isLocked) {
      lockedOffsetRef.current = null;
    }
    previousIsLockedRef.current = isLocked;
  }, [isLocked, selectedValues, type, dataArray, offset]);

  // Drive animation / landing based on spin & lock state
  useEffect(() => {
    if (!isInitialized) return;

    // If locked, cancel any animation and hold the locked offset
    if (isLocked) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (lockedOffsetRef.current !== null && offset !== lockedOffsetRef.current) {
        setOffset(lockedOffsetRef.current);
      }
      return;
    }

    // Spinning (and not locked) — animate continuously
    if (isSpinning) {
      let currentOffset = offset;
      const speed = 20;
      const totalHeight = ITEM_HEIGHT * dataArray.length;

      const animate = () => {
        currentOffset += speed;
        if (currentOffset >= totalHeight) {
          currentOffset = currentOffset % totalHeight;
        }
        setOffset(currentOffset);
        animationRef.current = requestAnimationFrame(animate);
      };

      animationRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
      };
    }

    // Idle (not spinning, not locked) — land on selected value if present.
    // Only snap if the current offset isn't already showing this value
    // (computed modulo dataArray length to respect the 4x repetition).
    const targetValue = selectedValues && selectedValues[type];
    if (targetValue) {
      const targetIndex = dataArray.indexOf(targetValue);
      if (targetIndex !== -1) {
        const currentRealIdx = ((Math.round(offset / ITEM_HEIGHT) % dataArray.length) + dataArray.length) % dataArray.length;
        if (currentRealIdx !== targetIndex) {
          const targetOffset = targetIndex * ITEM_HEIGHT;
          if (offset !== targetOffset) setOffset(targetOffset);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpinning, isLocked, isInitialized, selectedValues, type, dataArray]);

  // Click handler for an individual item inside the wheel. Tapping the
  // currently-centered item toggles the lock; tapping any other visible
  // item nudges the wheel onto that item (and notifies parent so the
  // new value persists when a spin or lock follows).
  const handleItemClick = (e, idx) => {
    e.stopPropagation();
    if (isSpinning) return;
    const dataLen = dataArray.length;
    if (!dataLen) return;
    const realIdx = ((idx % dataLen) + dataLen) % dataLen;
    const currentRealIdx = ((Math.round(offset / ITEM_HEIGHT) % dataLen) + dataLen) % dataLen;
    if (realIdx === currentRealIdx) {
      // Tap on the centered value → toggle lock.
      onToggleLock(type);
      return;
    }
    if (isLocked) {
      // Don't nudge while locked. User must tap the center to unlock first.
      return;
    }
    // Nudge to the clicked item's exact position (keeps the visual within
    // the user's current repetition rather than snapping back to the first).
    setOffset(idx * ITEM_HEIGHT);
    if (onNudge) onNudge(type, dataArray[realIdx]);
  };

  if (!isInitialized || items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2" data-testid={`slot-column-${type}-loading`}>
        <div className="text-sm tracking-widest text-gray-500 uppercase mb-2 font-black">{title}</div>
        <div className="relative w-44 h-52 border-2 rounded border-teal-900 bg-gray-950">
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xl font-black text-white">—</span>
          </div>
        </div>
        <div className="text-sm text-gray-600 mt-1 font-bold">tap to lock</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1 sm:gap-2" data-testid={`slot-column-${type}`}>
      <div className="text-[10px] sm:text-xs md:text-sm tracking-widest text-gray-500 uppercase mb-1 sm:mb-2 font-black">{title}</div>
      <div
        data-testid={`slot-toggle-${type}`}
        onClick={() => onToggleLock(type)}
        className={`relative w-24 h-36 sm:w-36 sm:h-44 md:w-44 md:h-52 border-2 rounded cursor-pointer transition-all duration-300 overflow-hidden ${
          isLocked
            ? 'border-yellow-600 bg-yellow-950/20'
            : 'border-teal-900 bg-gray-950 hover:border-teal-700'
        }`}
      >
        {/* Viewport mask */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-gray-950 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-950 to-transparent"></div>
        </div>

        {/* Highlight center item */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-5">
          <div className="w-full h-12 border-y border-yellow-600/30"></div>
        </div>

        {/* Scrolling items */}
        <div
          className="absolute w-full"
          style={{
            transform: `translateY(-${offset}px)`,
            top: '50%',
            marginTop: `-${ITEM_HEIGHT / 2}px`,
            transition: isSpinning ? 'none' : 'transform 800ms cubic-bezier(0.25, 0.1, 0.25, 1)'
          }}
        >
          {items.map((item, idx) => {
            const realIdx = ((idx % dataArray.length) + dataArray.length) % dataArray.length;
            const currentRealIdx = ((Math.round(offset / ITEM_HEIGHT) % dataArray.length) + dataArray.length) % dataArray.length;
            const isCenter = !isSpinning && Math.abs((offset / ITEM_HEIGHT) - idx) < 0.5;
            const canNudge = !isSpinning && !isLocked && realIdx !== currentRealIdx;
            return (
              <div
                key={idx}
                onClick={(e) => handleItemClick(e, idx)}
                data-testid={isCenter ? `slot-center-${type}` : undefined}
                className={`flex items-center justify-center text-center px-2 ${
                  isSpinning ? '' : 'cursor-pointer'
                } ${canNudge ? 'hover:text-gray-300' : ''}`}
                style={{ height: `${ITEM_HEIGHT}px`, position: 'relative', zIndex: 15 }}
              >
                <span className={`transition-all duration-200 font-bold ${
                  isCenter
                    ? 'text-xl font-black text-white'
                    : 'text-base text-gray-500'
                }`}>
                  {item}
                </span>
              </div>
            );
          })}
        </div>

        {isLocked && (
          <div className="absolute top-2 right-2 text-yellow-600 z-20" data-testid={`slot-lock-icon-${type}`}>
            <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
            </svg>
          </div>
        )}
      </div>
      <div className="text-[10px] sm:text-xs md:text-sm text-gray-600 mt-1 font-bold">
        {isLocked ? 'locked' : 'tap to lock'}
      </div>
    </div>
  );
};

const SlotMachine = ({ isSpinning, lockedSlots, onToggleLock, onNudge, selectedValues, systems, genres, vibes }) => {
  return (
    <div className="flex flex-row justify-center items-center gap-2 sm:gap-4 md:gap-6 px-2 overflow-x-auto" data-testid="slot-machine">
      <SlotColumn
        title="SYSTEM"
        type="system"
        isLocked={lockedSlots.system}
        dataArray={systems}
        isSpinning={isSpinning}
        onToggleLock={onToggleLock}
        onNudge={onNudge}
        selectedValues={selectedValues}
      />
      <SlotColumn
        title="GENRE"
        type="genre"
        isLocked={lockedSlots.genre}
        dataArray={genres}
        isSpinning={isSpinning}
        onToggleLock={onToggleLock}
        onNudge={onNudge}
        selectedValues={selectedValues}
      />
      <SlotColumn
        title="VIBE"
        type="vibe"
        isLocked={lockedSlots.vibe}
        dataArray={vibes}
        isSpinning={isSpinning}
        onToggleLock={onToggleLock}
        onNudge={onNudge}
        selectedValues={selectedValues}
      />
    </div>
  );
};

export default SlotMachine;
